// Shared building blocks for the two contribution-tree dialogs (admin model
// explorer FeatureContributionsDialog + Kohandustabel ComparableAnalysisDialog).
// The dialogs keep their own tree builders — their column semantics genuinely
// differ (subject-level + mean pairwise vs per-comparable) — but badges,
// value formatting, interaction splitting, filtering and sorting are one system.

export const KIND_BADGES = {
  interaction: {
    value: 'koosmõju',
    severity: 'warn',
    title:
      'EBM-i paaristerm (interaktsioon): kahe tunnuse koosmõju, millel pole üht toorväärtust. ' +
      'Kohandustabelis jaotub Shapley-jaotusega mõlema tunnuse gruppi.',
  },
  derived: {
    value: 'tuletatud',
    severity: 'secondary',
    title:
      'Vanema mudeli tuletatud korrutistunnus — mõju kuulub kahele tunnusperele, ' +
      'kuid grupeering suudab selle omistada vaid ühele (võimalik grupi-nihe).',
  },
  score: {
    value: 'skoor',
    severity: 'info',
    title:
      'Tuletatud järjestusskoor: serveris arvutatud aktiivsete toorkoodide keskmine ' +
      '(nt Hea + Väga hea → 4.5), vajadusel bucketi kaupa ümardatud. ' +
      'Tühjade koodidega objektil skoor puudub (mudel nägi tühja väärtust).',
  },
}

// Round long floats (month_sin & co) for display; keep ints/strings verbatim.
export const displayValue = value => {
  if (typeof value === 'number' && !Number.isInteger(value)) {
    return String(Math.round(value * 10000) / 10000)
  }
  return Array.isArray(value) ? JSON.stringify(value) : String(value)
}

// A pair term has no raw value of its own — show both constituents' values.
// `resolve` maps a feature name to its display value ('–' when unknown).
export const interactionValueFor = (feature, resolve) => {
  const parts = feature
    .split(/ & | x /)
    .map(part => `${part.trim()}=${resolve(part.trim())}`)
    .filter(part => !part.endsWith('=–'))
  return parts.length ? parts.join(' · ') : '–'
}

// Sort comparator: largest absolute contribution first (node.data.score).
export const byAbsScore = (a, b) => Math.abs(b.data.score) - Math.abs(a.data.score)

// ─── tree filtering (keep ancestors of matches) ──────────────────────────────

const nodeMatches = (node, needle) =>
  [node.data.label, node.data.feature, node.data.value].some(
    text => text != null && String(text).toLowerCase().includes(needle),
  )

export const filterTree = (nodes, needle) => {
  const kept = []
  for (const node of nodes) {
    const children = node.children ? filterTree(node.children, needle) : []
    if (children.length) {
      kept.push({ ...node, children })
    } else if (nodeMatches(node, needle)) {
      kept.push(node)
    }
  }
  return kept
}

// Expanded-keys map covering every internal node — used to auto-expand filter hits.
export const expandableKeys = nodes => {
  const keys = {}
  const walk = list => {
    for (const node of list) {
      if (node.children?.length) {
        keys[node.key] = true
        walk(node.children)
      }
    }
  }
  walk(nodes)
  return keys
}

// Sign-coded coloring in the Kohandustabel's adjustment convention:
// reverse=true means positive = green (subject better). Shared by the
// comparison table body and the analysis dialog.
export const adjustmentColorClass = (value, isBackground = false, reverse = false) => {
  if (value === undefined || value === null) return ''
  const num = Number.parseFloat(value)
  if (isNaN(num)) return ''
  if (num > 0)
    return isBackground
      ? reverse
        ? 'bg-green-500'
        : 'bg-red-500'
      : reverse
        ? 'text-green-600'
        : 'text-red-600'
  if (num < 0)
    return isBackground
      ? reverse
        ? 'bg-red-500'
        : 'bg-green-500'
      : reverse
        ? 'text-red-600'
        : 'text-green-600'
  if (num === 0) return isBackground ? 'bg-gray-400' : 'text-gray-600'
  return ''
}
